import {
  DEFAULT_RESEARCH_PAPER_PLAN,
  GALLERY_SNAPSHOT_SCHEMA,
  LEGACY_GALLERY_SNAPSHOT_SCHEMA,
  LEGACY_GALLERY_SNAPSHOT_V2_SCHEMA,
  type GallerySnapshot,
  type ResearchRequirementProjection
} from "@roboscientist/contracts";

export const MAX_GALLERY_PACKAGE_BYTES = 512 * 1024 * 1024;
const MAX_ENTRY_BYTES = 128 * 1024 * 1024;
const MAX_ENTRIES = 512;

export type GallerySnapshotFile = {
  name: string;
  size: number;
  arrayBuffer: () => Promise<ArrayBuffer>;
};

export type GallerySnapshotImport = {
  snapshot: GallerySnapshot;
  assets: Map<string, Blob>;
};

const record = (value: unknown): Record<string, unknown> | null =>
  value !== null && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;

const requirementDraftFields: Array<keyof ResearchRequirementProjection["draft"]> = [
  "topic", "objective", "baseline", "evaluation", "notes", "scope", "constraints",
  "maxGpus", "computeBudget", "computeNotes", "deliverables", "stopConditions"
];

const normalizeRequirement = (
  value: unknown,
  legacyV1: boolean,
  legacyV2: boolean
): ResearchRequirementProjection | null => {
  if (legacyV1) return null;
  if (value === null) return null;
  const requirement = record(value);
  const draft = record(requirement?.draft);
  const paperPlan = record(requirement?.paperPlan);
  const baseValid = requirement && draft
    && typeof requirement.topicId === "string" && Boolean(requirement.topicId)
    && typeof requirement.rootThreadId === "string" && Boolean(requirement.rootThreadId)
    && (requirement.experimentId === null || typeof requirement.experimentId === "string")
    && ["bounded", "persistent-goal", "continuous-improvement"].includes(String(requirement.runMode))
    && requirementDraftFields.every((field) => typeof draft[field] === "string")
    && typeof requirement.revision === "number" && Number.isInteger(requirement.revision) && requirement.revision >= 0
    && typeof requirement.sha256 === "string" && /^[a-f0-9]{64}$/.test(requirement.sha256)
    && requirement.frozen === true
    && typeof requirement.confirmedAt === "string" && Number.isFinite(Date.parse(requirement.confirmedAt))
    && Array.isArray(requirement.sourceFiles) && requirement.sourceFiles.every((file) => typeof file === "string");
  if (!baseValid) throw new Error("The Gallery snapshot Requirement is invalid.");
  const validPaperPlan = paperPlan
    && typeof paperPlan.enabled === "boolean"
    && typeof paperPlan.templateId === "string" && Boolean(paperPlan.templateId)
    && typeof paperPlan.writingProfile === "string" && Boolean(paperPlan.writingProfile);
  if (!validPaperPlan && !legacyV2) throw new Error("The Gallery snapshot Requirement Paper Plan is invalid.");
  return {
    ...requirement,
    paperPlan: validPaperPlan ? paperPlan : DEFAULT_RESEARCH_PAPER_PLAN
  } as ResearchRequirementProjection;
};

const lifecycle = new Set(["active", "paused", "blocked", "finalizing", "complete", "reopened"]);
const paperStatuses = new Set(["not_requested", "queued", "writing", "building", "needs_input", "blocked", "ready", "failed", "cancelled"]);

export function parseGallerySnapshot(value: unknown): GallerySnapshot {
  const root = record(value);
  const supportedSchemas = new Set<string>([GALLERY_SNAPSHOT_SCHEMA, LEGACY_GALLERY_SNAPSHOT_V2_SCHEMA, LEGACY_GALLERY_SNAPSHOT_SCHEMA]);
  if (!root || !supportedSchemas.has(String(root.schema))) {
    throw new Error("This file is not a supported RoboScientist Gallery snapshot.");
  }
  const legacyV1 = root.schema === LEGACY_GALLERY_SNAPSHOT_SCHEMA;
  const legacyV2 = root.schema === LEGACY_GALLERY_SNAPSHOT_V2_SCHEMA;
  const metadata = record(root.snapshot);
  const sourceProject = record(metadata?.sourceProject);
  if (!metadata || typeof metadata.id !== "string" || !metadata.id
    || typeof metadata.title !== "string" || !metadata.title
    || typeof metadata.summary !== "string"
    || typeof metadata.exportedAt !== "string" || !Number.isFinite(Date.parse(metadata.exportedAt))
    || !sourceProject || typeof sourceProject.id !== "string" || typeof sourceProject.name !== "string") {
    throw new Error("The Gallery snapshot metadata is incomplete.");
  }
  const topic = record(root.topic);
  if (!topic || !record(root.lineage)) throw new Error("The Gallery snapshot is missing its Topic or Evolution projection.");
  for (const field of ["experiments", "runs", "metrics", "reports"] as const) {
    if (!Array.isArray(root[field])) throw new Error(`The Gallery snapshot is missing ${field}.`);
  }
  const requirement = normalizeRequirement(root.requirement, legacyV1, legacyV2);
  const normalizedTopic = {
    ...topic,
    researchState: lifecycle.has(String(topic.researchState)) ? topic.researchState : "active",
    completionId: typeof topic.completionId === "string" ? topic.completionId : null,
    paperStatus: paperStatuses.has(String(topic.paperStatus)) ? topic.paperStatus : "not_requested"
  };
  const completionState = record(root.completionState);
  const fallbackCompletionState = {
    rootThreadId: String(topic.rootThreadId ?? topic.id ?? ""),
    state: normalizedTopic.researchState,
    cycle: 0,
    currentCompletionId: normalizedTopic.completionId,
    requestedAt: null,
    requestedTrigger: null,
    requestedTriggerEventId: null,
    requestedTerminalTurnId: null,
    updatedAt: String(topic.updatedAt ?? metadata.exportedAt),
    lastError: null
  };
  const rawPapers = Array.isArray(root.papers) ? root.papers : [];
  const papers = rawPapers.map((paper) => {
    const candidate = record(paper);
    if (!candidate || typeof candidate.paperId !== "string"
      || !/^[A-Za-z0-9][A-Za-z0-9._-]{0,127}$/.test(candidate.paperId)
      || typeof candidate.writingProfile !== "string" || !candidate.writingProfile) {
      throw new Error("The Gallery snapshot contains invalid paper metadata.");
    }
    const paperRoot = `papers/${candidate.paperId}/`;
    return {
      ...candidate,
      pdfPath: paperRoot + "paper.pdf",
      sourceZipPath: paperRoot + "source.zip",
      buildLogPath: paperRoot + "build.log",
      descriptorPath: paperRoot + "manifest.json"
    };
  });
  const arrays = {
    completions: Array.isArray(root.completions) ? root.completions : [],
    paperJobs: Array.isArray(root.paperJobs) ? root.paperJobs : [],
    papers
  };
  if (!legacyV1 && !legacyV2 && (!completionState || !Array.isArray(root.completions)
    || !Array.isArray(root.paperJobs) || !Array.isArray(root.papers))) {
    throw new Error("The Gallery v3 completion or paper projection is incomplete.");
  }
  return {
    ...root,
    schema: GALLERY_SNAPSHOT_SCHEMA,
    topic: normalizedTopic,
    requirement,
    completionState: completionState ? {
      ...fallbackCompletionState,
      ...completionState,
      requestedTrigger: completionState.requestedTrigger ?? null,
      requestedTriggerEventId: completionState.requestedTriggerEventId ?? null,
      requestedTerminalTurnId: completionState.requestedTerminalTurnId ?? null
    } : fallbackCompletionState,
    ...arrays
  } as GallerySnapshot;
}

const crcTable = (() => {
  const table = new Uint32Array(256);
  for (let index = 0; index < 256; index += 1) {
    let value = index;
    for (let bit = 0; bit < 8; bit += 1) value = (value & 1) ? (0xedb88320 ^ (value >>> 1)) : (value >>> 1);
    table[index] = value >>> 0;
  }
  return table;
})();

const crc32 = (payload: Uint8Array): number => {
  let value = 0xffffffff;
  for (const byte of payload) value = crcTable[(value ^ byte) & 0xff]! ^ (value >>> 8);
  return (value ^ 0xffffffff) >>> 0;
};

export const safeGalleryPackagePath = (value: string): string => {
  const normalized = value.replaceAll("\\", "/");
  if (!normalized || normalized.startsWith("/") || normalized.includes("\0")
    || normalized.split("/").some((part) => !part || part === "." || part === "..")) {
    throw new Error("The Gallery package contains an unsafe path.");
  }
  return normalized;
};

const mime = (name: string): string => name.endsWith(".pdf") ? "application/pdf"
  : name.endsWith(".zip") ? "application/zip"
    : name.endsWith(".json") ? "application/json" : "text/plain";

const unzipStored = (buffer: ArrayBuffer): Map<string, Blob> => {
  const bytes = new Uint8Array(buffer);
  const view = new DataView(buffer);
  let endOffset = -1;
  for (let offset = Math.max(0, bytes.length - 65_557); offset <= bytes.length - 22; offset += 1) {
    if (view.getUint32(offset, true) === 0x06054b50) endOffset = offset;
  }
  if (endOffset < 0) throw new Error("The Gallery ZIP end record is missing.");
  const count = view.getUint16(endOffset + 10, true);
  const centralSize = view.getUint32(endOffset + 12, true);
  const centralOffset = view.getUint32(endOffset + 16, true);
  if (count > MAX_ENTRIES || centralOffset + centralSize > endOffset) throw new Error("The Gallery ZIP directory is invalid.");
  const decoder = new TextDecoder("utf-8", { fatal: true });
  const files = new Map<string, Blob>();
  let cursor = centralOffset;
  let total = 0;
  for (let index = 0; index < count; index += 1) {
    if (cursor + 46 > bytes.length || view.getUint32(cursor, true) !== 0x02014b50) throw new Error("The Gallery ZIP directory entry is invalid.");
    const flags = view.getUint16(cursor + 8, true);
    const method = view.getUint16(cursor + 10, true);
    const checksum = view.getUint32(cursor + 16, true);
    const compressedSize = view.getUint32(cursor + 20, true);
    const size = view.getUint32(cursor + 24, true);
    const nameLength = view.getUint16(cursor + 28, true);
    const extraLength = view.getUint16(cursor + 30, true);
    const commentLength = view.getUint16(cursor + 32, true);
    const localOffset = view.getUint32(cursor + 42, true);
    if ((flags & 1) || method !== 0 || compressedSize !== size || size > MAX_ENTRY_BYTES) throw new Error("The Gallery ZIP uses an unsupported or unsafe entry.");
    const nameEnd = cursor + 46 + nameLength;
    if (nameEnd + extraLength + commentLength > bytes.length) throw new Error("The Gallery ZIP name is truncated.");
    const name = safeGalleryPackagePath(decoder.decode(bytes.subarray(cursor + 46, nameEnd)));
    if (files.has(name) || localOffset + 30 > bytes.length || view.getUint32(localOffset, true) !== 0x04034b50) throw new Error("The Gallery ZIP contains a duplicate or invalid local entry.");
    const localNameLength = view.getUint16(localOffset + 26, true);
    const localExtraLength = view.getUint16(localOffset + 28, true);
    const start = localOffset + 30 + localNameLength + localExtraLength;
    const end = start + size;
    if (end > bytes.length) throw new Error("The Gallery ZIP payload is truncated.");
    const payload = bytes.slice(start, end);
    if (crc32(payload) !== checksum) throw new Error("The Gallery ZIP checksum does not match.");
    total += payload.length;
    if (total > MAX_GALLERY_PACKAGE_BYTES) throw new Error("The Gallery ZIP expands beyond 512 MiB.");
    files.set(name, new Blob([payload], { type: mime(name) }));
    cursor = nameEnd + extraLength + commentLength;
  }
  return files;
};

export async function parseGallerySnapshotFile(file: GallerySnapshotFile): Promise<GallerySnapshotImport> {
  if (file.size > MAX_GALLERY_PACKAGE_BYTES) throw new Error(`${file.name} exceeds the 512 MiB package limit.`);
  try {
    const buffer = await file.arrayBuffer();
    const bytes = new Uint8Array(buffer);
    if (bytes.length >= 4 && bytes[0] === 0x50 && bytes[1] === 0x4b) {
      const assets = unzipStored(buffer);
      const snapshotBlob = assets.get("snapshot.json");
      if (!snapshotBlob || snapshotBlob.size > 16 * 1024 * 1024) throw new Error("The Gallery package is missing a bounded snapshot.json.");
      const snapshot = parseGallerySnapshot(JSON.parse(await snapshotBlob.text()));
      for (const paper of snapshot.papers) {
        for (const asset of [paper.pdfPath, paper.sourceZipPath, paper.buildLogPath, paper.descriptorPath]) {
          if (!assets.has(asset)) throw new Error("The Gallery package is missing paper asset " + asset + ".");
        }
      }
      assets.delete("snapshot.json");
      return { snapshot, assets };
    }
    return { snapshot: parseGallerySnapshot(JSON.parse(new TextDecoder("utf-8", { fatal: true }).decode(bytes))), assets: new Map() };
  } catch (error) {
    throw new Error(`${file.name}: ${error instanceof Error ? error.message : String(error)}`);
  }
}

export const sortGallerySnapshots = (snapshots: GallerySnapshot[]): GallerySnapshot[] =>
  [...snapshots].sort((left, right) =>
    Date.parse(right.snapshot.exportedAt) - Date.parse(left.snapshot.exportedAt)
  );
